import React from "react";
import {
  Box,
  Typography,
  ButtonBase,
} from "@mui/material";

/**
 * MonthlyRepeatOptions
 * --------------------
 * Independent component for monthly repeat configuration
 * - "Every N month(s)" with increment/decrement controls
 * - Clean, focused interface for monthly settings
 */

export function MonthlyRepeatOptions({ value, onChange, coral }) {
  const textDark = "rgb(20, 20, 20)";
  const rowBorder = "1px solid rgba(0,0,0,0.12)";
  const softGrey = "rgba(0,0,0,0.06)";

  const interval = clampInt(value.interval ?? 1, 1, 365);

  function patch(p) {
    onChange({ ...value, ...p });
  }

  function decInterval() {
    patch({ interval: clampInt(interval - 1, 1, 365) });
  }

  function incInterval() {
    patch({ interval: clampInt(interval + 1, 1, 365) });
  }

  const everyLabel = `Every ${interval} ${interval === 1 ? "month" : "months"}`;

  return (
    <>
      {/* Every N months row */}
      <Box
        sx={{
          border: rowBorder,
          borderRadius: 999,
          px: 1.5,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.25,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 900, color: textDark }}>
          {renderEveryLabel(everyLabel, coral)}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1px 1fr",
            alignItems: "center",
            height: 34,
            borderRadius: 999,
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.10)",
            background: softGrey,
            minWidth: 92,
          }}
        >
          <ButtonBase 
            onClick={decInterval} 
            sx={{ 
              height: "100%", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              "&:hover": { background: "rgba(0,0,0,0.05)" }
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: "rgba(0,0,0,0.50)" }}>−</Typography>
          </ButtonBase>

          <Box sx={{ width: 1, height: "70%", background: "rgba(0,0,0,0.15)" }} />

          <ButtonBase 
            onClick={incInterval} 
            sx={{ 
              height: "100%", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              "&:hover": { background: "rgba(0,0,0,0.05)" }
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: "rgba(0,0,0,0.50)" }}>+</Typography>
          </ButtonBase>
        </Box>
      </Box>

      {/* Monthly-specific info */}
      <Box sx={{ mb: 1.25 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: "rgba(0,0,0,0.35)" }}>
          Task will repeat on the same day each month
        </Typography>
      </Box>
    </>
  );
}

/* ---------------- helpers ---------------- */

function clampInt(n, min, max) {
  return Math.max(min, Math.min(max, Math.floor(n)));
}

function renderEveryLabel(label, coral) {
  // label: "Every 2 months", "Every 1 month", etc.
  const m = label.match(/^Every\s+(\d+)\s+(.*)$/);
  if (!m) return label;
  return (
    <>
      Every{" "}
      <Box component="span" sx={{ color: coral }}>
        {m[1]}
      </Box>{" "}
      {m[2]}
    </>
  );
}
